import { Formik } from 'formik';
import { StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton } from '../../components';
import { ReasonNextData } from '../types';
import { CheckboxGroup, CheckboxWithLabel } from '@uplift/ui-v2';
import { useSpecialties } from '@uplift/queries';
import { array, bool, object, ObjectSchema } from 'yup';
import { SpecialtyConcernCategoryEnum } from '@uplift/types';
import { Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

export type ReasonProps = {
  onNext: (data: ReasonNextData) => void;
  onBack: () => void;
  specialtyIds: string[];
  isBookingForSelf: boolean;
  banner?: React.ReactNode;
  hideDescription?: boolean;
  isRequired?: boolean;
};

type ReasonFormValues = {
  specialtyIds: string[];
  reasonNotHere: boolean;
};

export const Reason: React.VFC<ReasonProps> = props => {
  const { classes } = useReasonStyles();
  const { data: reasons = [] } = useSpecialties();

  const specificConcernReasons = reasons.filter(reason => reason.concernCategory === SpecialtyConcernCategoryEnum.specific);
  const generalConcernReasons = reasons.filter(reason => reason.concernCategory === SpecialtyConcernCategoryEnum.general);

  return (
    <Formik
      initialValues={{ specialtyIds: props.specialtyIds, reasonNotHere: false }}
      onSubmit={props.onNext}
      validationSchema={props.isRequired ? validationSchema : undefined}
      validateOnMount
    >
      {formik => (
        <StepContainer>
          {props.banner}
          <StepContent>
            <StepHeading>{props.isBookingForSelf ? 'What brings you in?' : 'What brings them in?'}</StepHeading>
            {!props.hideDescription && <StepDescription>When we know what’s important to you{props.isBookingForSelf ? '' : ' and your loved ones'}, we can recommend providers who specialize in that area.</StepDescription>}
            <StepForm onSubmit={formik.handleSubmit}>
              <div className={classes.checkboxSection}>
                <Typography variant="overline" className={classes.checkboxSectionLabel}>General concerns</Typography>
                <CheckboxGroup
                  className={classes.checkboxGroup}
                  name="specialtyIds"
                  options={generalConcernReasons.map(reason => ({
                    label: reason.name,
                    value: reason.id
                  }))}
                />
              </div>
              <div className={classes.checkboxSection}>
                <Typography variant="overline" className={classes.checkboxSectionLabel}>Specific concerns</Typography>
                <CheckboxGroup
                  className={classes.checkboxGroup}
                  name="specialtyIds"
                  options={specificConcernReasons.map(reason => ({
                    label: reason.name,
                    value: reason.id
                  }))}
                />
              </div>
              <div className={classes.checkboxSection}>
                <CheckboxWithLabel
                  className={classes.checkboxGroup}
                  name="reasonNotHere"
                  color="secondary"
                  label="My reason isn't here"
                  disabled={formik.values.specialtyIds.length > 0}
                />
              </div>
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid} />
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const useReasonStyles = makeStyles({ name: 'Reason' })(theme => ({
  checkboxSection: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(2),
    },
    '&:not(:first-of-type)': {
      paddingTop: theme.spacing(2),
    },

    margin: theme.spacing(0, -2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      margin: 'unset',
      paddingLeft: 0,
      paddingRight: 0,
    }
  },
  checkboxSectionLabel: {
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  checkboxGroup: {
    paddingLeft: theme.spacing(1.5)
  }
}));

const validationSchema = (): ObjectSchema<ReasonFormValues> => object({
  reasonNotHere: bool(),
  specialtyIds: array().when('reasonNotHere', {
    is: false,
    then: s => s.min(1, 'Please select at least one reason').required('Please select at least one reason'),
    otherwise: s => s.min(0).notRequired()
  })
});
